import React from 'react';
import get from 'lodash/get';
import toString from 'lodash/toString';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql } from '@apollo/react-hoc';
import { compose } from 'react-recompose';
import { FormattedMessage } from 'react-intl';

import Input from '../../../components/Input';
import InputRestrictMarkup from '../../../components/InputRestictMarkup';
import Modal from '../../../components/Modal';
import ClosableModal from '../../../components/Modal/ClosableModal';
import Button from '../../../components/Button';
import Header from '../../../components/Header';
import Icon from '../../../components/Icon';
import WYSIWYG from '../../../components/WYSIWYG';
import Loader from '../../../components/Loader';

import {
  setAboutUsData,
  addAboutUsPhotoS3ScannedURI,
  deleteAboutUs,
} from '../actions';
import InlineErrorMessage from './inlineErrorMessage';

import messages from '../messages';
import { SimpleLoadingComponent } from '../../../utils/apollo';
import ImageDropZoneWithCrop from '../ImageDropZoneWithCrop';

import './edit-modal-styles.css';

export const steps = {
  EDIT_ABOUT_US: 1,
  EDIT_PHOTO: 2,
};

export class EditAboutUsModalComponent extends React.Component {
  static propTypes = {
    onCloseModal: PropTypes.func.isRequired,
    viewer: PropTypes.object,
    openModal: PropTypes.bool,
    setAboutUsData: PropTypes.func,
    deleteAboutUs: PropTypes.func,
    aboutUsData: PropTypes.object,
    draftImageData: PropTypes.object,
    photoUpload: PropTypes.func,
    addAboutUsPhotoS3ScannedURI: PropTypes.func,
    hasRegisteredIntroChanges: PropTypes.bool,
  };

  state = {
    validQuote: true,
    validHeadline: true,
    validBody: true,
    currentStep: steps.EDIT_ABOUT_US,
    uploadingPhoto: false,
    nonPersistedCaption: null,
    quote: '',
    headline: '',
    hasChanges: false,
    showDeletionPopup: false,
    showDelete: true,
  };

  constructor(props) {
    const { viewer, hasRegisteredIntroChanges, aboutUsData: stateAboutUsData } = props;
    const aboutUsData = get(viewer, 'hubsSchoolProfiles', {});

    super(props);
    this.state.nonPersistedCaption = get(stateAboutUsData, 'body');
    this.state.quote = get(stateAboutUsData, 'quote');
    this.state.headline = get(stateAboutUsData, 'headline');

    /* istanbul ignore else */
    if (aboutUsData && !hasRegisteredIntroChanges) {
      this.props.setAboutUsData({ aboutUsData, hasRegisteredChanges: false });
      this.state.nonPersistedCaption = get(aboutUsData, 'body', '');
      this.state.quote = get(aboutUsData, 'quote', '');
      this.state.headline = get(aboutUsData, 'headline', '');

      if (get(aboutUsData, 'type', null) === null) {
        this.state.showDelete = false;
      }
    } else if (get(stateAboutUsData, 'type', null) === null) {
      this.state.showDelete = false;
    }
  }

  hasNewChanges = (headline, quote, body) => {
    const { aboutUsData } = this.props;
    const { aboutUsPhotoS3ScannedURI } = this.state;

    return (
      !!aboutUsPhotoS3ScannedURI ||
      toString(get(aboutUsData, 'headline')) !== toString(headline) ||
      toString(get(aboutUsData, 'quote')) !== toString(quote) ||
      toString(get(aboutUsData, 'body') || '<p><br /></p>') !== toString(body)
    );
  };

  onChangeHeadline = (e) => {
    const { quote, nonPersistedCaption } = this.state;

    if (e.target.value.length > 150) {
      this.setState({ validHeadline: false });
    } else {
      this.setState({
        validHeadline: true,
        headline: get(e, 'target.value', ''),
        hasChanges: this.hasNewChanges(e.target.value, quote, nonPersistedCaption),
      });
    }
  }

  onChangeQuote = (e) => {
    const { headline, nonPersistedCaption } = this.state;
    const value = get(e, 'target.value', '');
    if (value.length > 280) {
      this.setState({ validQuote: false });
    } else {
      this.setState({
        validQuote: true,
        quote: value,
        hasChanges: this.hasNewChanges(headline, value, nonPersistedCaption),
      });
    }
  }

  onChangeBody = (caption, isValid) => {
    const { headline, quote } = this.state;
    this.setState({
      validBody: isValid,
      nonPersistedCaption: caption,
      hasChanges: this.hasNewChanges(headline, quote, caption),
    });
  }

  onDelete = () => {
    this.setState({ showDeletionPopup: true });
  }

  onCloseDelete = () => {
    this.setState({ showDeletionPopup: false });
  }

  onConfirmedDelete = () => {
    this.props.deleteAboutUs();
    this.onCloseDelete();
    this.props.onCloseModal();
  };

  showImageDropZone = () => {
    this.setState({ currentStep: steps.EDIT_PHOTO });
  }

  goBackFromImageDropZone = () => {
    this.setState({ currentStep: steps.EDIT_ABOUT_US });
  }

  previewPhotoAndGoBack = async (ImageState) => {
    const { croppedImageBlobDataStr } = ImageState;
    this.setState({ uploadingPhoto: true });
    try {
      const uploadResult = await this.props.photoUpload({
        variables: {
          input: {
            imgDataStr: croppedImageBlobDataStr,
          },
        },
      });
      this.setState({ uploadingPhoto: false });
      if (!get(uploadResult, 'data.uploadPremiumHEMProfileImage.scanAndUploadSuccess')) {
        return;
      } else {
        this.setState({
          aboutUsPhotoS3ScannedURI: get(uploadResult, 'data.uploadPremiumHEMProfileImage.url'),
          hasChanges: true,
        });
      }
    } catch (err) {
      this.setState({ uploadingPhoto: false });
      return;
    }

    this.goBackFromImageDropZone();
  }

  renderModalContent = () => {
    const { aboutUsData, onCloseModal, draftImageData } = this.props;
    const { validQuote, validHeadline, validBody, currentStep, uploadingPhoto, headline, quote, hasChanges, showDelete } = this.state;
    const nonPersistedCaption = get(this.state, 'nonPersistedCaption');
    const displayHeadline = headline;
    const displayQuote = quote;
    const displayUrl = this.state.aboutUsPhotoS3ScannedURI || get(draftImageData, 'scannedPhotoUrl') || get(aboutUsData, 'img');

    switch (currentStep) {
      case steps.EDIT_ABOUT_US:
        return (
          <React.Fragment>
            <Modal.Content styleName="about-us-modal-content">
              <Header as="h1"><FormattedMessage {...messages.editAboutUsModalHeader} /></Header>
              <div styleName="about-us-top-container">
                <div styleName="about-us-fields-container">
                  <div styleName="about-us-modal-input-header">Headline</div>
                  <Input
                    id="about-us-headline-input"
                    styleName="about-us-modal-input"
                    fluid
                    placeholder="A mantra or a quick statement that embodies your college."
                    value={displayHeadline || ''}
                    onChange={this.onChangeHeadline}
                  />
                  <InlineErrorMessage
                    message="Headlines cannot be longer than 150 characters"
                    isVisible={!validHeadline}
                  />

                  <div styleName="about-us-modal-input-header">Quote</div>
                  <InputRestrictMarkup
                    id="about-us-quote-input"
                    styleName="about-us-modal-input"
                    fluid
                    placeholder="A moving quote about your college."
                    value={displayQuote || ''}
                    onChange={this.onChangeQuote}
                  />
                  <InlineErrorMessage
                    message="Quotes cannot be longer than 280 characters"
                    isVisible={!validQuote}
                  />
                </div>
                <div styleName="about-us-logo-container">
                  <Button
                    color="grey"
                    styleName="about-us-upload-photo-oval"
                    style={{ backgroundImage: `url(${displayUrl})` }}
                    onClick={this.showImageDropZone}
                  >
                    {!displayUrl && (
                      <p styleName="about-us-upload-photo-text">
                        <FormattedMessage {...messages.editAboutUsModalUploadPhoto} />
                      </p>
                    )}
                  </Button>
                  {displayUrl && (
                  <div styleName="edit-photo-button">
                    <Button basic onClick={this.showImageDropZone}>
                      <FormattedMessage {...messages.editAboutUsModalEditPhoto} />
                    </Button>
                  </div>
                  )}
                </div>
              </div>
              <div styleName="about-us-body-header">Body</div>
              <WYSIWYG
                id="about-us-content-input"
                className="edit-modal-wysiwyg-container"
                value={nonPersistedCaption}
                onChange={this.onChangeBody}
                limit={0}
                placeholder="Include biographical information, specialties, ratings, etc."
              />
            </Modal.Content>
            <Modal.Actions>
              {showDelete && (
                <Button
                  id="about-us-delete-button"
                  styleName="about-us-delete-button"
                  floated="left"
                  onClick={this.onDelete}
                  negative
                >
                  <FormattedMessage {...messages.editAboutUsDeleteCamel} />
                </Button>
              )}
              <Button id="about-us-cancel-button" styleName="about-us-cancel-button" onClick={onCloseModal} content="CANCEL" />
              <Button
                id="about-us-preview-button"
                styleName="about-us-preview-button"
                content="PREVIEW AND CONTINUE EDITING"
                disabled={!validQuote || !validHeadline || !validBody || !hasChanges}
                onClick={() => {
                  this.props.setAboutUsData({
                    aboutUsData: {
                      ...this.props.aboutUsData,
                      headline,
                      quote,
                      body: nonPersistedCaption,
                    },
                    hasRegisteredChanges: hasChanges,
                  });
                  /* istanbul ignore else */
                  if (this.state.aboutUsPhotoS3ScannedURI) {
                    this.props.addAboutUsPhotoS3ScannedURI(this.state.aboutUsPhotoS3ScannedURI);
                  }
                  onCloseModal();
                }}
                primary
              />
            </Modal.Actions>
          </React.Fragment>
        );
      case steps.EDIT_PHOTO:
        return (
          <React.Fragment>
            { uploadingPhoto ? (
              <div styleName="uploading-image-spinner">
                <Icon name="image outline" color="teal" size="massive" />
                <Loader active inline />
                <FormattedMessage {...messages.imageUploading} />
              </div>
            ) : (
              <ImageDropZoneWithCrop
                crop={{
                  aspect: 1,
                  width: 50,
                  height: 50,
                  x: 0,
                  y: 0,
                }}
                headerText={messages.cropPhotoInstructionsCircle}
                nevermindAction={this.goBackFromImageDropZone}
                nextButtonText={messages.continueEditingProfile}
                nextButtonAction={this.previewPhotoAndGoBack}
                uploaderInstructions1={messages.editPhotosAndVideosModalInstructions1}
                uploaderInstructions2={messages.editAboutUsPhotoModalInstructions2}
                roundCropper
                allowedTypes={['image/png', 'image/jpeg', 'image/jpg']}
              />
            )}
          </React.Fragment>
        );
      default:
        return (<Modal.Content><div id="invalid-step"><h3>Something went wrong...</h3></div></Modal.Content>);
    }
  }

  renderDeleteConfirm = () => (
    <Modal open>
      <React.Fragment>
        <Modal.Header styleName="deletion-popup-header">
          <FormattedMessage {...messages.editAboutUsConfirmDelete} />
        </Modal.Header>
        <Modal.Actions>
          <Button id="deletion-popup-cancel-button" onClick={this.onCloseDelete}>
            <FormattedMessage {...messages.editAboutUsCancel} />
          </Button>
          <Button
            id="deletion-popup-delete-button"
            primary
            onClick={this.onConfirmedDelete}
          >
            <FormattedMessage {...messages.editAboutUsDelete} />
          </Button>
        </Modal.Actions>
      </React.Fragment>
    </Modal>
  );

  render() {
    const { openModal, onCloseModal } = this.props;
    const { showDeletionPopup } = this.state;

    return (
      <ClosableModal
        open={openModal}
        onClose={onCloseModal}
        closeOnDimmerClick={false}
      >
        {this.renderModalContent()}
        {showDeletionPopup && this.renderDeleteConfirm()}
      </ClosableModal>
    );
  }
}

export const query = gql`
  query EditAboutUsModal {
    viewer {
      id
      hubsSchoolProfiles {
        img
        type
        body
        headline
        quote
        product_id
      }
    }
  }
`;

const mapStateToProps = (state) => ({
  aboutUsData: get(state, 'nonPersistedCollegeProfile.intro.aboutUsData'),
  hasRegisteredIntroChanges: get(state, 'nonPersistedCollegeProfile.intro.hasRegisteredChanges'),
  draftImageData: get(state, 'nonPersistedCollegeProfile.intro.draftImageData'),
});

export default compose(
  connect(mapStateToProps, {
    setAboutUsData,
    addAboutUsPhotoS3ScannedURI,
    deleteAboutUs,
  }),
  SimpleLoadingComponent(graphql(query, {
    options: { fetchPolicy: 'network-only' },
  })),
)(EditAboutUsModalComponent);
